/* ============================================================================
   Livera 5 Reasons Bloat — vanilla JS to power the Replo-exported components.
   Replaces the Replo runtime. Handles: carousels, FAQ accordion, marquees,
   plan picker, add-to-cart, nutrition-label gallery jump, CTA scroll.
   No dependencies. Drop-in.

   Behaviors were captured from the LIVE Replo page (2026-06-03):
   - Plan card click updates only the hidden /cart/add form (no visual change
     on the live page; we additionally sync the radio for a11y/CSS hooks).
   - "View Nutrition Label" slides the buybox gallery to the supplement-facts
     slide (no modal).
   - The two role="link" CTAs are inert on the live page (no Replo action
     configured). Here they smooth-scroll to the buybox — deliberate deviation,
     documented in README-5-reasons-bloat.md.
   ============================================================================ */
(function () {
  'use strict';

  /* --------- 1) CAROUSELS  data-replo-carousel="true" --------- */
  function initCarousel(root) {
    var tracks = root.querySelectorAll('[data-replo-part="slide-track"]');
    if (!tracks.length) return;
    // slides per track; responsive exports can ship >1 track per carousel
    var primary = tracks[0];
    for (var t = 0; t < tracks.length; t++) {
      if (tracks[t].offsetParent !== null) { primary = tracks[t]; break; }
    }
    // exclude infinite-loop clones (SSR bakes them with inline opacity:0) —
    // navigating onto one shows a blank slide
    var slides = primary.querySelectorAll('[data-replo-part="slide"]:not([data-is-cloned="true"])');
    // Some Replo exports mark EVERY slide data-is-cloned="true" (no "real" slides),
    // so the filter above returns nothing and the carousel freezes on one image with
    // dead arrows. Fall back to all slides, deduped by source index (one per image).
    if (!slides.length) {
      var seen = {};
      slides = Array.prototype.filter.call(
        primary.querySelectorAll('[data-replo-part="slide"]'),
        function (s) {
          var k = s.getAttribute('data-slide-source-index');
          if (k == null) return true;
          if (seen[k]) return false;
          seen[k] = true; return true;
        }
      );
    }
    var prevBtn = root.querySelector('[data-replo-part="nav-button"][data-direction="previous"]');
    var nextBtn = root.querySelector('[data-replo-part="nav-button"][data-direction="next"]');
    var liveRegion = root.querySelector('.replo-sr-only');
    if (!slides.length) return;

    var idx = 0;
    var total = slides.length;

    function update() {
      var target = slides[idx];
      if (!target) return;
      var offset = target.offsetLeft - primary.offsetLeft;
      tracks.forEach(function (tr) {
        tr.style.transition = 'transform .45s ease';
        tr.style.transform = 'translate3d(' + (-offset) + 'px,0,0)';
      });
      // SSR bakes inactive slides at opacity:0 (the absent Replo runtime would toggle
      // this). Reveal the current slide and hide the rest so opacity-based exports
      // advance too instead of translating onto a blank slide.
      for (var k = 0; k < slides.length; k++) {
        slides[k].style.opacity = (k === idx) ? '1' : '0';
      }
      if (liveRegion) liveRegion.textContent = 'Slide ' + (idx + 1) + ' of ' + total;
    }

    function go(delta) { idx = (idx + delta + total) % total; update(); }
    function goTo(i) { idx = ((i % total) + total) % total; update(); }

    // expose for the nutrition-label button
    root.__liveraCarousel = { go: go, goTo: goTo, slides: slides };

    if (prevBtn) prevBtn.addEventListener('click', function (e) { e.preventDefault(); go(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function (e) { e.preventDefault(); go(1); });

    // touch / swipe
    var startX = null;
    primary.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
    primary.addEventListener('touchend', function (e) {
      if (startX == null) return;
      var dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 40) go(dx > 0 ? -1 : 1);
      startX = null;
    });

    window.addEventListener('resize', update);
    requestAnimationFrame(update);
  }

  /* --------- 2) FAQ ACCORDION  data-replo-collapsible="true" --------- */
  /* The SSR panels ship as <div class="replo-animated" hidden> (Radix-style).
     We must remove the [hidden] attribute on open — height tricks alone never
     reveal them. The bundle's own slideDown/slideUp keyframes animate height
     via --radix-collapsible-content-height, which the Replo runtime used to
     set; we set it ourselves before opening. */
  function initCollapsible(root) {
    var trigger = root.querySelector('[aria-expanded]');
    if (!trigger) return;
    var panelId = trigger.getAttribute('aria-controls');
    var panel = panelId ? document.getElementById(panelId) : null;
    if (!panel) panel = trigger.nextElementSibling;
    if (!panel) return;

    var open = trigger.getAttribute('aria-expanded') === 'true';
    var closeTimer = null;

    function toggle() {
      open = !open;
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      root.setAttribute('data-state', open ? 'open' : 'closed');
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      if (open) {
        panel.removeAttribute('hidden');
        panel.style.setProperty('--radix-collapsible-content-height', panel.scrollHeight + 'px');
        panel.setAttribute('data-state', 'open');
      } else {
        panel.style.setProperty('--radix-collapsible-content-height', panel.scrollHeight + 'px');
        panel.setAttribute('data-state', 'closed');
        // re-hide only after the 200ms slideUp animation finishes
        closeTimer = setTimeout(function () { panel.setAttribute('hidden', ''); }, 220);
      }
    }

    trigger.addEventListener('click', toggle);
    trigger.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
    });
  }

  /* --------- 3) MARQUEE  data-replo-marquee-root="true" --------- */
  /* Replo's marquee CSS already drives the animation; just keep it running. */
  function initMarquee(root) {
    var track = root.querySelector('[data-replo-marquee-track="true"]');
    if (track) track.style.animationPlayState = 'running';
  }

  /* --------- 4) CTA SCROLL → BUYBOX --------- */
  function scrollToBuybox() {
    var bb = document.querySelector('[data-replo-product-container]');
    if (bb) bb.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  function initCtaScroll(btn) {
    btn.addEventListener('click', function (e) { e.preventDefault(); scrollToBuybox(); });
  }

  /* --------- 5) PLAN PICKER  [data-livera-plan-picker] --------- */
  /* Mirrors the live Replo behavior: clicking a card updates the hidden
     /cart/add form. We also check the card's radio and mirror the wiring onto
     the primary CTA so both purchase paths stay in sync. */
  function getCartForm() {
    return document.querySelector('form[data-type="add-to-cart-form"]');
  }
  function applyPlan(card) {
    var variantId = card.getAttribute('data-variant-id');
    var qty = card.getAttribute('data-quantity') || '1';
    var sellingPlan = card.getAttribute('data-selling-plan') || '';
    var form = getCartForm();
    if (form) {
      var idInput = form.querySelector('input[name="id"]');
      var qtyInput = form.querySelector('input[name="quantity"]');
      var spInput = form.querySelector('input[name="selling_plan"]');
      if (idInput) idInput.value = variantId;
      if (qtyInput) qtyInput.value = qty;
      if (spInput) spInput.value = sellingPlan;
    }
    var primaryCta = document.querySelector('[data-livera-primary-cta]');
    if (primaryCta) {
      primaryCta.setAttribute('data-variant-id', variantId);
      primaryCta.setAttribute('data-quantity', qty);
      if (sellingPlan) primaryCta.setAttribute('data-selling-plan', sellingPlan);
      else primaryCta.removeAttribute('data-selling-plan');
    }
  }
  function initPlanPicker(container) {
    var cards = container.querySelectorAll('[data-livera-plan]');
    if (!cards.length) return;
    cards.forEach(function (card) {
      card.addEventListener('click', function () {
        cards.forEach(function (c) {
          c.removeAttribute('data-selected');
          var r = c.querySelector('input[type="radio"]');
          if (r) r.checked = false;
        });
        card.setAttribute('data-selected', 'true');
        var radio = card.querySelector('input[type="radio"]');
        if (radio) radio.checked = true;
        applyPlan(card);
      });
    });
    var def = container.querySelector('[data-livera-plan][data-default]') || cards[0];
    if (def) { def.setAttribute('data-selected', 'true'); applyPlan(def); }
  }

  /* --------- 6) ADD TO CART  [data-livera-primary-cta] --------- */
  /* Clear-then-add so the chosen plan is the only line item, then straight to
     checkout — same flow as the exclusive-offer export. */
  function initAddToCart(btn) {
    btn.addEventListener('click', async function (e) {
      e.preventDefault();
      var variantId = btn.getAttribute('data-variant-id');
      var qty = parseInt(btn.getAttribute('data-quantity') || '1', 10) || 1;
      if (!variantId) {
        console.warn('[Livera] no variant id on add-to-cart button — scrolling to buybox', btn);
        scrollToBuybox();
        return;
      }
      btn.setAttribute('disabled', 'true');
      try {
        await fetch('/cart/clear.js', { method: 'POST', headers: { 'Accept': 'application/json' } });
        var item = { id: parseInt(variantId, 10), quantity: qty };
        var sellingPlan = btn.getAttribute('data-selling-plan');
        if (sellingPlan) item.selling_plan = parseInt(sellingPlan, 10);
        var res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [item] })
        });
        if (!res.ok) throw new Error('cart add failed: ' + res.status);
        window.location.href = '/checkout';
      } catch (err) {
        console.error('[Livera] add to cart error', err);
        btn.removeAttribute('disabled');
        alert('Could not add to cart. Please try again.');
      }
    });
  }

  /* --------- 7) VIEW NUTRITION LABEL  [data-livera-nutrition] --------- */
  /* Live behavior: slides the buybox gallery to the supplement-facts slide. */
  function initNutrition(btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var carouselRoot = btn.closest('[data-replo-carousel="true"]') ||
        document.querySelector('[data-replo-carousel="true"]');
      if (!carouselRoot || !carouselRoot.__liveraCarousel) return;
      var api = carouselRoot.__liveraCarousel;
      for (var i = 0; i < api.slides.length; i++) {
        var img = api.slides[i].querySelector('img[alt*="supplement facts" i], img[alt*="Supplement Facts"]');
        if (img) { api.goTo(i); return; }
      }
      // fallback: known asset UUID of the nutrition label image
      for (var j = 0; j < api.slides.length; j++) {
        if (api.slides[j].innerHTML.indexOf('22b02c86') !== -1) { api.goTo(j); return; }
      }
    });
  }

  /* --------- 8) ENTRANCE ANIMATIONS (alchemy-fade / alchemy-slide-y) --------- */
  /* Replo bakes entrance animations as paused CSS animations with
     animation-fill-mode:both and --initial-opacity:0 — without the runtime the
     elements are frozen INVISIBLE at the `from` keyframe (this is why whole
     sections looked missing). Replicate the runtime: play the animation when
     the element scrolls into view. */
  function initEntranceAnimations() {
    var entrance = [];
    document.querySelectorAll('#replo-fullpage-element *').forEach(function (el) {
      var cs = getComputedStyle(el);
      var name = cs.animationName || '';
      if (name.indexOf('alchemy-') === -1) return;
      if (cs.animationPlayState.indexOf('paused') === -1) return;
      if (name.indexOf('marquee') !== -1) {
        el.style.animationPlayState = 'running'; // tickers run immediately
        return;
      }
      entrance.push(el);
    });
    if (!('IntersectionObserver' in window)) {
      entrance.forEach(function (el) { el.style.animationPlayState = 'running'; });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) {
          en.target.style.animationPlayState = 'running';
          io.unobserve(en.target);
        }
      });
    }, { threshold: 0.1 });
    entrance.forEach(function (el) { io.observe(el); });
  }

  /* --------- 9) HOMEPAGE REVIEWS SECTION (UGC videos + arrows) --------- */
  /* The reviews block is the homepage's markup verbatim; these two handlers
     are copied from the homepage's inline scripts. */
  function initHomeReviews() {
    document.querySelectorAll('[data-ugc-card]').forEach(function (card) {
      var vid = card.querySelector('video');
      if (!vid) return;
      function play() { vid.play(); card.classList.add('playing'); }
      function pause() { vid.pause(); card.classList.remove('playing'); }
      card.addEventListener('click', function () {
        if (card.classList.contains('playing')) pause(); else play();
      });
      vid.addEventListener('ended', pause);
    });
    var t = document.getElementById('ugcTrack');
    if (!t) return;
    var rm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    function go(dir) {
      var c = t.querySelector(':scope > *');
      if (!c) return;
      var g = parseFloat(getComputedStyle(t).columnGap || getComputedStyle(t).gap) || 16;
      var step = c.getBoundingClientRect().width + g;
      var max = t.scrollWidth - t.clientWidth;
      var tgt;
      if (dir > 0) { tgt = t.scrollLeft >= max - 2 ? 0 : Math.min(max, t.scrollLeft + step); }
      else { tgt = t.scrollLeft <= 2 ? max : Math.max(0, t.scrollLeft - step); }
      if (rm) { t.scrollLeft = tgt; } else { t.scrollTo({ left: tgt, behavior: 'smooth' }); }
    }
    document.querySelectorAll('[data-carousel="ugc"]').forEach(function (b) {
      b.addEventListener('click', function (e) { e.preventDefault(); go(Number(b.dataset.dir)); });
    });
  }


  /* --------- 10) HOMEPAGE BUYBOX (plan picker + AJAX cart) --------- */
  /* Verbatim from the homepage buybox script. Gallery parts (bbMain/bbThumbs)
     no-op here — our page keeps the Replo gallery. */
  function initHomeBuybox() {
    try {
        // The legacy .plan-based buybox was replaced by the pack-based .lvhb block,
        // which ships its own controller + submit handler inline. With no .plan cards
        // on the page, this controller has nothing to drive and would only double-bind
        // the form's submit (→ double /cart/add). Bail so .lvhb is the sole controller.
        if (!document.querySelector('.plan')) return;

        // Plan selection + form data sync
        const bbVariant = document.getElementById('bbVariant');
        const bbQty = document.getElementById('bbQty');
        const bbPlan = document.getElementById('bbPlan');
        function syncPlanToForm(p) {
          if (!bbVariant) return;
          bbVariant.value = p.dataset.variant || '';
          bbQty.value = p.dataset.qty || '1';
          const plan = p.dataset.plan || '';
          bbPlan.value = plan;
          // Remove selling_plan from form if not subscription (one-time purchase)
          if (!plan) bbPlan.removeAttribute('name'); else bbPlan.name = 'selling_plan';
          const ctaPriceEl = document.querySelector('.bb-main-cta-price');
          if (ctaPriceEl) {
            const suffix = p.dataset.ctaSuffix || '';
            ctaPriceEl.textContent = suffix ? ' — ' + suffix : '';
          }
        }
        const plans = Array.from(document.querySelectorAll('.plan'));
        function selectPlan(p, focus) {
          plans.forEach(x => {
            const sel = x === p;
            x.classList.toggle('selected', sel);
            x.setAttribute('aria-checked', sel ? 'true' : 'false');
            x.setAttribute('tabindex', sel ? '0' : '-1');
          });
          syncPlanToForm(p);
          if (focus) p.focus();
        }
        plans.forEach((p, i) => {
          p.addEventListener('click', () => selectPlan(p));
          p.addEventListener('keydown', (e) => {
            if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); selectPlan(p); return; }
            if (e.key === 'ArrowDown' || e.key === 'ArrowRight') { e.preventDefault(); selectPlan(plans[(i + 1) % plans.length], true); }
            if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') { e.preventDefault(); selectPlan(plans[(i - 1 + plans.length) % plans.length], true); }
            if (e.key === 'Home') { e.preventDefault(); selectPlan(plans[0], true); }
            if (e.key === 'End') { e.preventDefault(); selectPlan(plans[plans.length - 1], true); }
          });
        });
        const initiallySelected = document.querySelector('.plan.selected');
        if (initiallySelected) syncPlanToForm(initiallySelected);
      
        // Cart submit — AJAX /cart/add.js → /checkout (inline error on failure)
        const cartForm = document.getElementById('buyboxForm');
        const cartCTA = cartForm && cartForm.querySelector('.bb-main-cta');
        const cartCTALabel = cartCTA && cartCTA.querySelector('.bb-main-cta-label');
        const cartStatus = document.getElementById('bbStatus');
        if (cartForm && cartCTA) {
          const cartSubmit = async (e) => {
            e.preventDefault();
            cartCTA.dataset.loading = 'true';
            cartCTA.disabled = true;
            if (cartStatus) cartStatus.textContent = 'Adding to cart…';
            const fd = new FormData(cartForm);
            try {
              // Clear cart first so only the selected plan goes to checkout
              await fetch('/cart/clear.js', { method: 'POST', headers: { 'Accept': 'application/json' } });
              const res = await fetch('/cart/add.js', { method: 'POST', body: fd, headers: { 'Accept': 'application/json' } });
              if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                cartCTA.dataset.loading = 'false';
                cartCTA.disabled = false;
                const msg = err.description || err.message || 'Could not add to cart. Please try again.';
                if (cartStatus) cartStatus.textContent = msg;
                if (cartCTALabel) cartCTALabel.textContent = 'Try Again';
                return;
              }
              if (cartStatus) cartStatus.textContent = 'Added! Redirecting…';
              // 1-pouch one-time → intermediary Subscribe & Save offer; everything else → checkout
              var _v = document.getElementById('bbVariant'), _p = document.getElementById('bbPlan'), _q = document.getElementById('bbQty');
              var _otp1 = _v && _v.value === '43887589195842' && (!_p || !_p.value) && (!_q || _q.value === '1');
              window.location.href = _otp1 ? '/pages/checkout-upsell' : '/checkout';
            } catch (_) {
              // Network error — fall back to native form POST (uses return_to=/checkout)
              cartForm.removeEventListener('submit', cartSubmit);
              cartForm.submit();
            }
          };
          cartForm.addEventListener('submit', cartSubmit);
        }
      
        // Carousels — scroll by one card width per arrow click + edge disabled state
        const tracks = {ing: document.getElementById('ingTrack'), rev: document.getElementById('revTrack')};
        const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        function updateArrowState(key) {
          const t = tracks[key];
          if (!t) return;
          const max = t.scrollWidth - t.clientWidth - 1;
          const prev = document.querySelector(`[data-carousel="${key}"][data-dir="-1"]`);
          const next = document.querySelector(`[data-carousel="${key}"][data-dir="1"]`);
          if (prev) prev.toggleAttribute('disabled', t.scrollLeft <= 1);
          if (next) next.toggleAttribute('disabled', t.scrollLeft >= max);
        }
        document.querySelectorAll('[data-carousel]').forEach(btn => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            const t = tracks[btn.dataset.carousel];
            if (!t) return;
            const card = t.querySelector(':scope > *');
            const cardWidth = card ? card.getBoundingClientRect().width : t.clientWidth * 0.85;
            const gap = parseFloat(getComputedStyle(t).columnGap || getComputedStyle(t).gap) || 16;
            const step = (cardWidth + gap) * Number(btn.dataset.dir);
            const target = Math.max(0, Math.min(t.scrollWidth - t.clientWidth, t.scrollLeft + step));
            if (reduceMotion) { t.scrollLeft = target; }
            else { t.scrollTo({left: target, behavior:'smooth'}); }
          });
        });
        Object.keys(tracks).forEach(key => {
          const t = tracks[key];
          if (!t) return;
          let scheduled = false;
          t.addEventListener('scroll', () => {
            if (scheduled) return;
            scheduled = true;
            requestAnimationFrame(() => { scheduled = false; updateArrowState(key); });
          }, {passive: true});
          updateArrowState(key);
        });
      
        // Buy box gallery — thumbs, arrows, and dots all stay in sync
        const bbMain = document.getElementById('bbMain');
        const bbThumbs = Array.from(document.querySelectorAll('.buybox-thumb'));
        const bbDots = Array.from(document.querySelectorAll('.bb-pagedot'));
        let bbIdx = 0;
        const bbMainVideo = document.getElementById('bbMainVideo');
        function bbSelect(i) {
          bbIdx = (i + bbThumbs.length) % bbThumbs.length;
          bbThumbs.forEach((t, k) => t.classList.toggle('active', k === bbIdx));
          bbDots.forEach((d, k) => d.classList.toggle('active', k === bbIdx));
          const thumb = bbThumbs[bbIdx];
          const src = thumb?.dataset.src;
          const type = thumb?.dataset.type || 'image';
          if (type === 'video' && bbMainVideo) {
            if (bbMain) bbMain.style.display = 'none';
            bbMainVideo.style.display = 'block';
            const source = bbMainVideo.querySelector('source');
            if (source && !source.src.endsWith(src.split('/').pop())) { source.src = src; bbMainVideo.load(); }
          } else {
            if (bbMainVideo) { bbMainVideo.pause(); bbMainVideo.style.display = 'none'; }
            if (bbMain && src) { bbMain.style.display = 'block'; bbMain.src = src; }
          }
          thumb?.scrollIntoView({behavior:'smooth', inline:'nearest', block:'nearest'});
        }
        bbThumbs.forEach((th, i) => th.addEventListener('click', () => bbSelect(i)));
        bbDots.forEach((dot, i) => dot.addEventListener('click', () => bbSelect(i)));
        document.querySelector('.bb-arrow-prev')?.addEventListener('click', () => bbSelect(bbIdx - 1));
        document.querySelector('.bb-arrow-next')?.addEventListener('click', () => bbSelect(bbIdx + 1));
      
      
    } catch (e) { console.error('[Livera] home buybox init', e); }
  }

  /* --------- BOOT --------- */
  function boot() {
    document.querySelectorAll('[data-replo-carousel="true"]').forEach(initCarousel);
    document.querySelectorAll('[data-replo-collapsible="true"]').forEach(initCollapsible);
    document.querySelectorAll('[data-replo-marquee-root="true"]').forEach(initMarquee);
    document.querySelectorAll('[data-livera-plan-picker]').forEach(initPlanPicker);
    document.querySelectorAll('[data-livera-primary-cta]').forEach(initAddToCart);
    document.querySelectorAll('[data-livera-nutrition]').forEach(initNutrition);
    // Inert Replo CTAs (role="link") → smooth-scroll to buybox.
    document.querySelectorAll('#replo-fullpage-element button[role="link"]').forEach(initCtaScroll);
    initEntranceAnimations();
    initHomeReviews();
    initHomeBuybox();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
